import { useStripePaymentV2 } from '@common/hooks/useStripePaymentV2';
import { useAppSelector } from '@common/redux/hooks';
import { profileSelector } from '@common/redux/selectors/auth';
import { get } from 'lodash';
import { useCallback, useState } from 'react';
import { Linking } from 'react-native';
import { getActions, getItemListClick } from '../../shared';
import { getValueBinding } from '../shared';
import { STRIPE_CONNECT_ID_FID } from './constant';

export const KEY_MODE_STRIPE = {
  TEST: 'test',
  LIVE: 'live',
};

const initialState = {
  text: { status: null, message: null },
};

export type ReceivedProps = {
  attributes: Record<string, any>;
  id: string;
  data: any;
  dependencies: Record<string, any>;
  locale: 'en' | 'ja';
  screenUuid: string;
  appConfig: {
    clientUrl: string;
  };
  isInCustomList: boolean;
  initId: string;
  record: any;
  onPress: (actions: any[], metadata: any) => void;
};

const useStripePayment = (props: ReceivedProps) => {
  const { data, attributes, isInCustomList, onPress, record, appConfig } =
    props;
  const [stripeConfiguration, setStripeConfiguration] = useState<{
    publicKey: string;
    clientId: string;
  } | null>(null);
  const profile = useAppSelector(profileSelector);
  const { getStripeConfiguration } = useStripePaymentV2();
  const {
    title: { enabled, text },
  } = attributes;

  const bindingValue = getValueBinding(
    isInCustomList ? props.initId : props.id,
    isInCustomList ? props?.record : data,
    props
  );

  const typeConnection = get(bindingValue, 'typeConnection') as
    | 'anonymous'
    | 'accountHolder';
  const accountConnectId: string =
    typeConnection === 'anonymous'
      ? get(bindingValue, 'accountConnectId', '')
      : get(profile, STRIPE_CONNECT_ID_FID, '');
  const emailBuyer = get(bindingValue, 'email.emailBuyer') as string;
  const amount = Number(get(bindingValue, 'paymentOptions.paymentAmount')) || 0;
  const currency = get(bindingValue, 'paymentOptions.typeCurrency', 'jpy') as
    | 'jpy'
    | 'usd';
  const paymentDescription = get(
    bindingValue,
    'changedescription.description'
  ) as string;
  const submitButtonText = get(
    bindingValue,
    'submitButton.text',
    'Pay Now'
  ) as string;
  const connectButtonText = get(
    bindingValue,
    'connectButton.text',
    'Connect to Stripe'
  ) as string;
  const disconnectButtonText = get(
    bindingValue,
    'disconnectButton.text',
    'Disconnect'
  ) as string;

  const [card, setCard] = useState<{
    text: {
      status:
        | 'succeeded'
        | 'requires_payment_method'
        | 'notValidOrRequired'
        | null;
      message: string | null;
    };
  }>(initialState);

  const disabledButton = card.text.status === 'notValidOrRequired';

  const handleAction = (actionType: 'successActions' | 'failedActions') => {
    onPress &&
      onPress(getActions(props, actionType), {
        itemListClick: getItemListClick(record),
      });
  };

  const updateStatusCard = (
    status:
      | 'succeeded'
      | 'requires_payment_method'
      | 'notValidOrRequired'
      | null,
    message: string | null
  ) => setCard({ text: { status, message } });

  const retrieveStripeConfiguration = useCallback(async (appId: string) => {
    const res = await getStripeConfiguration({ appId });
    setStripeConfiguration(res);
    return res;
  }, []);

  const connectToStripe = ({ appId }: { appId: string }) => {
    if (!stripeConfiguration?.clientId) {
      alert('Stripe configuration is not found');
      return;
    }
    const url = new URL('https://connect.stripe.com/oauth/v2/authorize');
    url.searchParams.set('client_id', stripeConfiguration.clientId);
    url.searchParams.set(
      'redirect_uri',
      `${appConfig.clientUrl}/verify-stripe-token-v2`
    );
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', 'read_write');
    url.searchParams.set(
      'state',
      JSON.stringify({
        appId,
        userId: profile?.id ?? '',
      })
    );
    Linking.openURL(url.toString());
  };

  return {
    ...props,
    paymentAmount: amount,
    typeConnection,
    submitButtonText,
    connectButtonText,
    disconnectButtonText,
    enabledTitle: enabled,
    titleText: text,
    accountConnectId,
    card,
    emailBuyer,
    stripeConfiguration,
    disabledButton,
    currency,
    paymentDescription,
    handleAction,
    updateStatusCard,
    retrieveStripeConfiguration,
    connectToStripe,
  };
};

export type Props = ReturnType<typeof useStripePayment>;

export default useStripePayment;
